import type { Router } from "vue-router"

const RECOVERY_KEY = "pitchlane:dynamic-import-recovery"
const ASSET_PROBE_TIMEOUT_MS = 5_000
const DYNAMIC_IMPORT_CONTEXT = "dynamic_import_recovery"
const errorState = new WeakMap<object, "pending" | "handled" | "reported">()
let activeRecovery: object | undefined

interface RecoveryRecord {
    targetPath: string
    sourceRelease?: string
    attemptedAt: number
}

interface RecoveryDependencies {
    currentHref: string
    online: boolean
    fetchFn: typeof fetch
    storage: Storage
    assign: (targetPath: string) => void
    captureException: (error: unknown, hint: Record<string, unknown>) => void
    release?: string
    now?: () => number
    navigationSequence?: number
    currentNavigationSequence?: () => number
}

export interface DynamicImportRecoveryRuntime {
    currentHref: () => string
    online: () => boolean
    fetchFn: typeof fetch
    storage: Storage
    assign: (targetPath: string) => void
    captureException: (error: unknown, hint: Record<string, unknown>) => void
    release?: string
    onResult?: (result: DynamicImportRecoveryResult, error: unknown) => void
}

export interface DynamicImportRecoveryResult {
    handled: boolean
    outcome:
        | "not-candidate"
        | "reloading"
        | "repeat-failure"
        | "asset-still-available"
        | "asset-probe-failed"
        | "guard-unavailable"
        | "superseded"
}

function getErrorMessage(error: unknown): string | undefined {
    if (!(error instanceof Error) || error.name !== "TypeError") return undefined
    return error.message
}

export function getDynamicImportAssetUrl(
    error: unknown,
    currentHref: string,
    online: boolean,
): URL | undefined {
    if (!online) return undefined

    const message = getErrorMessage(error)
    if (!message) return undefined

    const match = message.match(
        /^(?:Failed to fetch dynamically imported module|error loading dynamically imported module): (https?:\/\/\S+)$/i,
    )
    if (!match) return undefined

    let assetUrl: URL
    let currentUrl: URL
    try {
        assetUrl = new URL(match[1])
        currentUrl = new URL(currentHref)
    } catch {
        return undefined
    }

    if (assetUrl.origin !== currentUrl.origin) return undefined
    if (!/^\/assets\/.+-[A-Za-z0-9_-]{8,}\.js$/.test(assetUrl.pathname)) return undefined

    return assetUrl
}

export function markDynamicImportErrorPending(error: unknown): void {
    if (typeof error === "object" && error !== null) errorState.set(error, "pending")
}

function markDynamicImportError(error: unknown, state: "handled" | "reported"): void {
    if (typeof error === "object" && error !== null) errorState.set(error, state)
}

export function filterHandledDynamicImportEvent<T extends { contexts?: Record<string, unknown> }>(
    event: T,
    hint?: { originalException?: unknown },
): T | null {
    const original = hint?.originalException
    if (typeof original !== "object" || original === null || !errorState.has(original)) return event

    return event.contexts?.[DYNAMIC_IMPORT_CONTEXT] ? event : null
}

function readRecovery(storage: Storage): RecoveryRecord | undefined {
    const raw = storage.getItem(RECOVERY_KEY)
    if (!raw) return undefined

    try {
        const record = JSON.parse(raw) as RecoveryRecord
        if (typeof record.targetPath !== "string" || typeof record.attemptedAt !== "number") {
            storage.removeItem(RECOVERY_KEY)
            return undefined
        }
        return record
    } catch {
        storage.removeItem(RECOVERY_KEY)
        return undefined
    }
}

function reportFailure(
    error: unknown,
    assetUrl: URL,
    targetPath: string,
    outcome: Exclude<DynamicImportRecoveryResult["outcome"], "not-candidate" | "reloading">,
    dependencies: RecoveryDependencies,
    extra: Record<string, unknown> = {},
): DynamicImportRecoveryResult {
    markDynamicImportError(error, "reported")
    dependencies.captureException(error, {
        contexts: {
            [DYNAMIC_IMPORT_CONTEXT]: {
                outcome,
                asset_url: assetUrl.href,
                target_path: targetPath,
                current_route: new URL(dependencies.currentHref).pathname,
                release: dependencies.release ?? "unknown",
                ...extra,
            },
        },
    })
    return { handled: true, outcome }
}

function supersede(error: unknown): DynamicImportRecoveryResult {
    markDynamicImportError(error, "handled")
    return { handled: true, outcome: "superseded" }
}

export async function recoverFromStaleDynamicImport(
    error: unknown,
    targetPath: string,
    dependencies: RecoveryDependencies,
): Promise<DynamicImportRecoveryResult> {
    const assetUrl = getDynamicImportAssetUrl(error, dependencies.currentHref, dependencies.online)
    if (!assetUrl) return { handled: false, outcome: "not-candidate" }

    markDynamicImportErrorPending(error)

    const navigationIsCurrent = () =>
        dependencies.navigationSequence === undefined ||
        dependencies.currentNavigationSequence === undefined ||
        dependencies.navigationSequence === dependencies.currentNavigationSequence()
    if (!navigationIsCurrent()) return supersede(error)

    // Only the newest navigation may finish the one allowed recovery attempt.
    // Older probes retain their local token and become harmless when superseded.
    const recovery = {}
    activeRecovery = recovery

    let previous: RecoveryRecord | undefined
    try {
        previous = readRecovery(dependencies.storage)
    } catch (storageError) {
        if (activeRecovery === recovery) activeRecovery = undefined
        return reportFailure(error, assetUrl, targetPath, "guard-unavailable", dependencies, {
            storage_error: storageError instanceof Error ? storageError.name : "unknown",
        })
    }

    if (previous) {
        if (activeRecovery === recovery) activeRecovery = undefined
        return reportFailure(error, assetUrl, targetPath, "repeat-failure", dependencies, {
            source_release: previous.sourceRelease ?? "unknown",
            attempted_at: new Date(previous.attemptedAt).toISOString(),
            attempted_target: previous.targetPath,
        })
    }

    const probeController = new AbortController()
    let probeTimeout: ReturnType<typeof setTimeout> | undefined
    let response: Response
    try {
        const timeout = new Promise<never>((_, reject) => {
            probeTimeout = setTimeout(() => {
                probeController.abort()
                reject(new DOMException("Asset probe timed out", "TimeoutError"))
            }, ASSET_PROBE_TIMEOUT_MS)
        })
        response = await Promise.race([
            dependencies.fetchFn(assetUrl, {
                method: "HEAD",
                cache: "no-store",
                credentials: "same-origin",
                signal: probeController.signal,
            }),
            timeout,
        ])
    } catch (probeError) {
        if (activeRecovery !== recovery) return supersede(error)
        activeRecovery = undefined
        return reportFailure(error, assetUrl, targetPath, "asset-probe-failed", dependencies, {
            probe_error: probeError instanceof Error ? probeError.name : "unknown",
        })
    } finally {
        if (probeTimeout) clearTimeout(probeTimeout)
    }

    if (activeRecovery !== recovery || !navigationIsCurrent()) return supersede(error)

    if (response.status !== 404 && response.status !== 410) {
        activeRecovery = undefined
        return reportFailure(error, assetUrl, targetPath, "asset-still-available", dependencies, {
            probe_status: response.status,
        })
    }

    const now = (dependencies.now ?? Date.now)()
    try {
        dependencies.storage.setItem(
            RECOVERY_KEY,
            JSON.stringify({
                targetPath,
                sourceRelease: dependencies.release,
                attemptedAt: now,
            } satisfies RecoveryRecord),
        )
    } catch (storageError) {
        activeRecovery = undefined
        return reportFailure(error, assetUrl, targetPath, "guard-unavailable", dependencies, {
            storage_error: storageError instanceof Error ? storageError.name : "unknown",
        })
    }

    activeRecovery = undefined
    markDynamicImportError(error, "handled")
    dependencies.assign(targetPath)
    return { handled: true, outcome: "reloading" }
}

export function clearSuccessfulDynamicImportRecovery(
    targetPath: string,
    storage: Storage,
    navigationFailure?: unknown,
): void {
    if (navigationFailure) return

    try {
        const record = readRecovery(storage)
        if (record?.targetPath === targetPath) storage.removeItem(RECOVERY_KEY)
    } catch {
        // A storage failure must not interfere with successful navigation.
    }
}

export function installDynamicImportRecovery(
    router: Router,
    runtime: DynamicImportRecoveryRuntime,
): void {
    const navigationsSeenByBeforeEach = new WeakSet<object>()
    const navigationSequences = new WeakMap<object, number>()
    let currentNavigationSequence = 0

    router.beforeEach((to) => {
        currentNavigationSequence += 1
        navigationsSeenByBeforeEach.add(to)
        navigationSequences.set(to, currentNavigationSequence)
        // Any newer navigation makes a pending probe obsolete, even when the
        // newer navigation succeeds or fails for a different reason.
        activeRecovery = undefined
    })

    router.afterEach((to, _from, failure) => {
        if (navigationsSeenByBeforeEach.has(to)) {
            // This navigation already invalidated older probes when it began.
            // Do not let its late completion invalidate a newer navigation.
            navigationsSeenByBeforeEach.delete(to)
        } else {
            // Duplicate navigation can skip beforeEach, but is still newer
            // user intent and must supersede an older pending probe.
            currentNavigationSequence += 1
            activeRecovery = undefined
        }
        clearSuccessfulDynamicImportRecovery(to.fullPath, runtime.storage, failure)
    })

    router.onError((error, to) => {
        const navigationSequence = navigationSequences.get(to)
        if (navigationSequence === undefined) {
            console.error("Uncaught Navigation Error", error)
            return
        }

        void recoverFromStaleDynamicImport(error, to.fullPath, {
            currentHref: runtime.currentHref(),
            online: runtime.online(),
            fetchFn: runtime.fetchFn,
            storage: runtime.storage,
            assign: runtime.assign,
            captureException: runtime.captureException,
            release: runtime.release,
            navigationSequence,
            currentNavigationSequence: () => currentNavigationSequence,
        }).then((result) => {
            runtime.onResult?.(result, error)
            if (!result.handled) console.error("Uncaught Navigation Error", error)
        })
    })
}
